import SubscribableService from './subscribable-service';
import AmplifyService from 'amplify';

class AppDataService extends SubscribableService {
  constructor() {
    super();
    this.frontEndConfig = {};
  }

  setFrontEndConfig(frontEndConfig) {
    this.frontEndConfig = frontEndConfig;
  }

  getAppMode() {
    const appMode = this.frontEndConfig.AppMode;
    if (appMode === undefined) {
      return window.SS_GLOBALS.appMode;
    }
    return appMode;
  }

  getDataFromCache(cacheKey, safe) {
    const cacheData = AmplifyService.store(`ss_cache_${cacheKey}`);
    if (safe && !cacheData) {
      return {};
    }
    return cacheData;
  }

  setDataToCache(cacheKey, data) {
    return AmplifyService.store(`ss_cache_${cacheKey}`, data);
  }

  setEntireCache(cacheData) {
    Object.keys(cacheData).forEach(key =>
      this.setDataToCache(key, cacheData[key])
    );
    this.onChange();
  }

  setAuthInfoDataToCache(data) {
    return AmplifyService.store('AuthInfo', data);
  }

  getAuthInfo() {
    return AmplifyService.store('AuthInfo') || {};
  }

  getMinimumSemVersion() {
    const authInfo = this.getAuthInfo();
    const { minimumSemVersion } = authInfo;
    return minimumSemVersion;
  }

  getServices() {
    return this.getDataFromCache('Services');
  }

  getFulfillmentServices() {
    return this.getDataFromCache('FillServices');
  }

  getProviders() {
    return this.getDataFromCache('ShippingProviders');
  }

  getProviderAccountsByProviderId(providerId) {
    return this.getProviderAccounts().filter(acct => {
      return acct.ProviderID === providerId;
    });
  }

  // Should be active accounts only
  getProviderAccounts() {
    return this.getDataFromCache('LightSellerProviders');
  }

  getCarriers() {
    return this.getDataFromCache('Carriers');
  }

  getAllCarriers(safe) {
    const allCarriers = this.getDataFromCache('AllCarriers');
    if (!safe) {
      return allCarriers;
    }
    return allCarriers || [];
  }

  getCarrierPackageTypes() {
    return this.getDataFromCache('CarrierPackages');
  }

  getCustomPackages() {
    return this.getDataFromCache('CustomPackages');
  }

  getFeatureMask() {
    return this.getDataFromCache('FeatureMask');
  }

  getShipstationConnectWorkstations() {
    return this.getDataFromCache('Workstations') || [];
  }

  isRunningLabelApi() {
    return this.getAppMode() === 'labelapi';
  }

  getIsShipStation() {
    return !this.isRunningLabelApi();
  }

  getIsShipEngine() {
    return this.isRunningLabelApi();
  }

  getUserSettings(safe) {
    return this.getDataFromCache('UserSettings', safe);
  }

  getSettings() {
    const settings = this.getDataFromCache('Settings');
    return settings || {};
  }

  getSetting(name) {
    return (this.getSettings() || {})[name];
  }

  getCountries() {
    return this.getDataFromCache('Countries');
  }

  saveUserSettingsToCache(userSettings) {
    this.setDataToCache('UserSettings', userSettings);
    this.onChange();
  }

  getEnvironment() {
    return this.getDataFromCache('Instance');
  }

  hasMultipleCarrierProviders() {
    const carrierIdMap = {};
    const providers = this.getProviders();
    if (providers === undefined) {
      return false;
    }

    providers.forEach(provider => {
      if (carrierIdMap[provider.CarrierID] === undefined) {
        carrierIdMap[provider.CarrierID] = 0;
      }
      carrierIdMap[provider.CarrierID] += 1;
    });

    return Object.keys(carrierIdMap).some(
      carrierID => carrierIdMap[carrierID] > 1
    );
  }

  getConfirmations() {
    return this.getDataFromCache('Confirmation');
  }

  getWarehouses() {
    return this.getDataFromCache('Warehouses');
  }

  getFulfillmentProviders() {
    return this.getDataFromCache('FillProviders');
  }

  getSellerFillProviders() {
    return this.getDataFromCache('SellerFillProviders');
  }

  getPresets() {
    return this.getDataFromCache('Presets');
  }

  getMarketplaces() {
    return this.getDataFromCache('Marketplaces');
  }

  getAutoProvisionedProviders() {
    return this.getDataFromCache('AutoProvisionedOnlyProviders');
  }

  getShippingPresets() {
    return this.getDataFromCache('Presets') || [];
  }

  getWallets() {
    return this.getDataFromCache('LightWallets') || [];
  }

  getRecaptchaSiteKey() {
    return this.frontEndConfig.recaptchaSiteKey;
  }

  getAuth0ClientCredentials() {
    return this.frontEndConfig.auth0Credentials;
  }

  getDisableRedirectToV3() {
    return this.frontEndConfig.disableRedirectToV3;
  }

  getShippingOptions() {
    return this.getDataFromCache('ShippingOptions') || [];
  }
}

export default new AppDataService();
